import { ErrorResponse } from 'proto/Api/Error';

import {
    CallOptions,
    ContentType,
    FetchError,
    RequestDataEncoder,
    ResponseDecoder,
} from '../types';

import { buildRequestUrl } from './fetch';

const DEFAULT_HEADERS: Record<ContentType, Record<string, string>> = {
    [ContentType.Protobuf]: {
        Accept: ContentType.Protobuf,
        'Content-Type': ContentType.Protobuf,
        'X-Requested-With': 'protobuf',
    },
    [ContentType.Json]: {
        Accept: ContentType.Json,
        'Content-Type': ContentType.Json,
        'X-Requested-With': 'json',
    },
};

const buildRpcBody = <RequestType>(
    requestData: RequestType,
    encoder: RequestDataEncoder<RequestType>,
    rpcContentType: ContentType
): BodyInit =>
    rpcContentType === ContentType.Protobuf
        ? encoder.encode(requestData).finish()
        : JSON.stringify(encoder.fromJSON(requestData));

export const buildRpcRequest = <RequestType, UrlParams>(
    encoder: RequestDataEncoder<RequestType>,
    rpcContentType: ContentType,
    {
        url,
        requestOptions,
        requestData,
        queryParams,
        urlParams,
    }: CallOptions<RequestType, UrlParams>
) =>
    new Request(buildRequestUrl(url, queryParams, urlParams), {
        ...requestOptions,
        method: 'POST',
        body: requestData
            ? buildRpcBody(requestData, encoder, rpcContentType)
            : null,
        headers: {
            ...requestOptions?.headers,
            ...DEFAULT_HEADERS[rpcContentType],
        },
    });

export async function parseRpcResponse<ResponseType>(
    decoder: ResponseDecoder<ResponseType>,
    rpcContentType: ContentType,
    response: Response
): Promise<ResponseType> {
    if (response.headers.get('Content-Type') !== rpcContentType) {
        throw new FetchError(
            'Request Accept and response Content-Type headers do not match'
        );
    }

    return rpcContentType === ContentType.Protobuf
        ? decodeBinaryResponse(
              new Uint8Array(await response.arrayBuffer()),
              decoder
          )
        : decoder.fromJSON(await response.json());
}

function decodeBinaryResponse<ResponseType>(
    data: Uint8Array,
    decoder: ResponseDecoder<ResponseType>
): ResponseType {
    try {
        return decoder.decode(data);
    } catch (e) {
        if (e instanceof RangeError) {
            const errorData = ErrorResponse.decode(data);

            return decoder.fromJSON(ErrorResponse.toJSON(errorData));
        }
        throw e;
    }
}
